import type { TimedAtom } from '@ctcells/api'
import type { BatchCommand, BatchStatus, RecoverDecision } from '@ctcells/contract'
import { serverText, t, type MessageKey } from '@ctcells/i18n'
import { useState } from 'react'

import { Button } from '../atoms/Button.tsx'
import { Field } from '../atoms/Field.tsx'
import { Icon } from '../atoms/Icon.tsx'
import { Modal } from '../atoms/Modal.tsx'
import { Segmented } from '../atoms/Segmented.tsx'
import { useSubmitValidation } from '../atoms/useSubmitValidation.ts'
import { TextInput } from '../atoms/TextInput.tsx'
import { ValueRows } from '../atoms/ValueRows.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../screens/access.ts'
import { CutAtomChecks, TimedAtomPick } from '../screens/batches/resumeRoute.tsx'
import {
  allChecked,
  type ResumeCheck,
  type ResumeRoute,
} from '../screens/batches/resumeRouteModel.ts'

// `.vissue`(실패 문구 한 줄)가 여기 산다 — 계정 조작 대화와 같은 자리를 쓴다
import '../screens/resource.css'
// 중지 창의 경고 표지 · 물음 글자(`.strip-confirm__*`)
import './batchStrip.css'

/**
 * 상시 작업 줄의 **확인 대화** — 되돌릴 수 없는 넷만 여기를 지난다.
 *
 * | 조작        | 경로                        | 본문                                        |
 * | ----------- | --------------------------- | ------------------------------------------- |
 * | `stop`      | `POST /batches/{id}/stop`   | `IrreversibleCommandEnvelope` — `reason` 필수(화면은 선택 · 비우면 기본 사유) |
 * | `recover`   | `POST /batches/{id}/recover`| `RecoverDecision` — `discard`·`confirm_completed` 는 `reason` 필수 |
 *
 * 🔴 **복구 판정은 대화 안에 펼친 구획 선택기(`Segmented`) + 고른 판정 이름의 확인 버튼 하나다**(eric 2026-10-14
 *    「판정 클릭하면 나오게 말고..」 — 대화 안 드롭다운이 대화 밑으로 잘렸다 → 「너 생각에 좋은 방법 구현해줘」).
 *    판정 셋을 나란히 버튼으로 두면 되돌릴 수 없는 「폐기」가 「재개」 바로 옆에서 한 번에 나간다 — 고르기와 보내기를 가른다.
 *    판정은 위 세 칸이 말하므로 아래 버튼은 「취소 · 확인」이다(eric 2026-10-14 「재개라고 하고.. 밑에는 취소 확인이어야
 *    하지 않아?」 — 같은 말을 두 번 적지 않는다). 폐기일 때만 확인이 위험색이다. 사유 칸은 계약이 요구할 때만 선다.
 * | `force`     | `DELETE /control-token?force=true` | `IrreversibleCommandEnvelope` — `reason` 필수 · admin(`settings`) 한정 |
 *
 * ⚠ 내 락 반납(`DELETE /control-token`)은 여기 오지 않는다 — 묻지 않고 고정 사유로 바로 나간다(eric 2026-10-29).
 *
 * 🔴 **시작 · 일시정지 · 재개는 여기 오지 않는다.** 되돌릴 수 있고 본문이 `command_id` 하나다 —
 *    확인 대화를 붙이면 급할 때 손이 한 번 더 간다.
 *    ⚠ 예외 하나 — **[재개]에서 끊긴 시간 원자 방식을 고를 수 있을 때**(`resume_timed_atom_choices` 가 둘)만 `resume` 대화가 선다.
 *    고를 것이 없으면 종전대로 바로 나간다(백엔드 0.28.15 · `resumeRoute.tsx`).
 *
 * ## 확인 재개(`resume_route=confirm` · 백엔드 0.28.15 · eric 2026-10-22)
 *
 * 복구 판정의 «재개» 칸이 «멈춘 동작부터 다시»(`rerun_cut_atom`)로 바뀐다 — 서버가 그냥 재개를 받지 않는다.
 * 그 칸을 고르면 확인할 것(`resume_checks` · 없으면 일반 한 칸)이 체크 목록으로 서고 **전부 체크해야** 확인이 눌린다.
 * 본문에 `physical_checked: true` · `physical_checks`(체크한 것) · 필요하면 `timed_atom` 을 싣는다.
 * 🔴 **E-STOP 은 절대 여기 오지 않는다**(정본 07 §2.7). 누르면 바로 나간다.
 * 🔴 **실패하면 대화가 닫히지 않는다.** `errorMessage` 가 그 자리에 뜨고 사용자가 다시 누른다 —
 *    닫아 버리면 「보냈는데 안 됐다」를 사용자가 모른 채 넘어간다.
 *
 * 모양은 사용자 관리의 계정 조작 대화(`screens/admin/UserDetailScreen.tsx`)와 **같다** —
 * 판별 유니온 하나로 대화를 통합하고, 계약 필수값이 채워지기 전에는 확인 버튼이 비활성 + 사유다.
 */

export type StripConfirmKind = 'stop' | 'recover' | 'force' | 'resume'

/** 확인까지 끝난 조작. 🔴 계약 필수값은 **여기 도달한 시점에 이미 채워져 있다** */
export type StripCommandAction =
  | { kind: 'stop'; reason: string }
  | {
      kind: 'recover'
      decision: RecoverDecision['decision']
      reason: string | undefined
      /** `rerun_cut_atom` 에서만 — 사람이 현장 확인 목록을 다 체크했다 */
      physicalChecked?: true
      physicalChecks?: readonly ResumeCheck[]
      timedAtom?: TimedAtom
    }
  | { kind: 'release'; reason: string; force: boolean }
  | { kind: 'resume'; timedAtom?: TimedAtom }

const TITLE: Record<StripConfirmKind, MessageKey> = {
  stop: 'strip.stopTitle',
  recover: 'strip.recoverTitle',
  force: 'lock.forceReleaseTitle',
  resume: 'strip.resumeTitle',
}

/** 확인 버튼은 **그 동작 이름**을 그대로 쓴다. 복구만 「확인」이다 — 판정 이름은 위 세 칸(`DECISIONS`)이 이미 말한다 */
const CONFIRM_LABEL: Record<Exclude<StripConfirmKind, 'recover' | 'stop'>, MessageKey> = {
  force: 'action.forceReleaseLock',
  resume: 'action.resume',
}

/**
 * 복구 판정 3택 — 계약 `RecoverDecision.decision` enum **그대로**다. 🔴 프론트가 기본값을 「안전한 쪽」으로
 * 바꾸지 않는다 — 무엇이 안전한지는 사람이 정한다. 위험색은 시료를 버리는 폐기 하나다.
 */
const DECISIONS: readonly { value: RecoverDecision['decision']; labelKey: MessageKey }[] = [
  { value: 'resume', labelKey: 'action.resume' },
  { value: 'discard', labelKey: 'action.discard' },
  { value: 'confirm_completed', labelKey: 'action.confirmCompleted' },
]

/** 확인 재개 멈춤의 3택 — 그냥 «재개» 자리에 «멈춘 동작부터 다시»가 선다(서버가 그냥 재개를 받지 않는다) */
const CONFIRM_DECISIONS: readonly { value: RecoverDecision['decision']; labelKey: MessageKey }[] = [
  { value: 'rerun_cut_atom', labelKey: 'action.rerunCutAtom' },
  { value: 'discard', labelKey: 'action.discard' },
  { value: 'confirm_completed', labelKey: 'action.confirmCompleted' },
]

/** 장비를 다시 움직이는 판정 — 비상정지 중이면 확인이 막히고, 시간 원자 방식을 고를 수 있다 */
const MOVING_DECISIONS: ReadonlySet<RecoverDecision['decision']> = new Set([
  'resume',
  'rerun_cut_atom',
])

export interface StripCommandDialogProps {
  kind: StripConfirmKind
  /** 대상 작업 id. 제어락 조작에는 없다 */
  batchId?: string | undefined
  /** 대화를 연 버튼과 **같은 판정**을 확인 버튼에도 건다 */
  access?: ScreenAccess
  allowedCommands?: readonly BatchCommand[] | undefined
  /**
   * 작업 상태(`Batch.status`). 🔴 **사유 문구만 고른다** — 활성은 여전히 `allowedCommands` 다.
   * 멈춘 작업(`paused` · `error`)에서 중지가 집합에 없으면 「[복구 › 폐기]로 끝냅니다」를 말한다(작업 줄 [중지]와 같은 키).
   */
  batchStatus?: BatchStatus | undefined
  /**
   * 비상정지 중인가(`GET /safety` · `emergency_stop`). 🔴 켜져 있으면 복구 › **재개**의 확인이 비활성 + 사유다 —
   * 서버 발행 게이트가 거절한다(eric 2026-10-14). 폐기 · 완료 확정은 이 값으로 막지 않는다(폐기는 비상정지 중에도 받는다).
   */
  estopped?: boolean | undefined
  /** 요청이 날아가 있는 동안. 같은 명령을 두 번 보내지 못하게 한다 */
  pending?: boolean | undefined
  /** 실패 문구. 컨테이너가 `errorMessage()`(에러 코드 사전)로 만들어 넘긴다 */
  errorMessage?: string | undefined
  /** 멈춘 배치를 어떤 단추로 잇는가(`Batch.resume_route`). `confirm` 이면 복구 3택의 첫 칸이 «멈춘 동작부터 다시»다 */
  resumeRoute?: ResumeRoute | undefined
  /** 끊김이 따로 요구하는 현장 확인(`Batch.resume_checks`) */
  resumeChecks?: readonly ResumeCheck[] | undefined
  /** 끊긴 시간 원자 방식 — 둘일 때만 고르기가 선다(`Batch.resume_timed_atom_choices`) */
  timedAtomChoices?: readonly TimedAtom[] | undefined
  onCancel: () => void
  onSubmit: (action: StripCommandAction) => void
}

export function StripCommandDialog({
  kind,
  batchId,
  access = {},
  estopped = false,
  pending,
  errorMessage,
  resumeRoute,
  resumeChecks = [],
  timedAtomChoices = [],
  allowedCommands,
  batchStatus,
  onCancel,
  onSubmit,
}: StripCommandDialogProps) {
  const confirmRoute = resumeRoute === 'confirm'
  const decisions = confirmRoute ? CONFIRM_DECISIONS : DECISIONS
  const [reason, setReason] = useState('')
  const [decision, setDecision] = useState<RecoverDecision['decision']>(
    confirmRoute ? 'rerun_cut_atom' : 'resume',
  )
  const [checked, setChecked] = useState<ReadonlySet<string>>(new Set())
  const [timedAtom, setTimedAtom] = useState<TimedAtom>('restart')
  const rerun = kind === 'recover' && decision === 'rerun_cut_atom'
  /* 시간 원자 방식은 장비를 다시 움직이는 재개 · 확인 재개에서만 — 고를 것이 둘일 때만 칸이 선다 */
  const timedShown =
    timedAtomChoices.length >= 2 &&
    (kind === 'resume' || (kind === 'recover' && MOVING_DECISIONS.has(decision)))

  /**
   * 🔴 **사유가 언제 필수인가는 계약이 정한다.** 중지 · 반납 · 강제 해제는 봉투가
   *    `IrreversibleCommandEnvelope` 라 항상 필수이고, 복구는 `resume` 만 예외다. ⚠ 중지만 화면에서 선택이다(아래).
   *    비워서 보내면 422 이므로 **화면이 먼저 막는다** — 서버 왕복으로 알아내지 않는다.
   */
  const reasonRequired =
    kind !== 'stop' && kind !== 'resume' && (kind !== 'recover' || decision !== 'resume')
  /*
   * 🔴 **중지는 사유가 «선택»이다**(eric 2026-10-26 「사유는 옵션으로 하고..」). 계약은 여전히 필수(`minLength: 1`)라
   *    비워 두면 기본 사유(`strip.stopReasonDefault`)를 실어 보낸다 — 감사 원장에 그 글자가 그대로 남는다.
   */
  const reasonShown = reasonRequired || kind === 'stop'
  const validation = useSubmitValidation((value: string) => ({
    reason: reasonRequired && value.trim() === '' ? t('validation.reasonRequired') : undefined,
  }))
  const reasonField = validation.field('reason')

  /** 락 조작은 `settings`(강제 해제) 또는 `control`(반납) 권한 축이다 */
  const perm = kind === 'force' ? 'settings' : 'control'

  const moving = kind === 'resume' || (kind === 'recover' && MOVING_DECISIONS.has(decision))
  const batchCommand = kind === 'stop' || kind === 'recover' || kind === 'resume' ? kind : undefined
  const accessGate = gate(access, {
    perm,
    needsLock: batchCommand !== undefined,
    stateOk: batchCommand === undefined || (allowedCommands?.includes(batchCommand) ?? false),
    /*
     * 창이 열린 뒤 작업이 멈추면(running → paused · error) 서버가 중지를 집합에서 뺀다 — 끝내는 길은 [복구 › 폐기]다
     * (core `FROM_STOP`). 작업 줄 [중지] 단추(`BatchStrip` `reason.stopWhileHalted`)와 같은 문구를 쓴다.
     */
    ...(kind === 'stop' && (batchStatus === 'paused' || batchStatus === 'error')
      ? { stateReason: 'reason.stopWhileHalted' as const }
      : {}),
  })
  const confirmGate: ActionGate = accessGate.disabled
    ? accessGate
    : pending
      ? { disabled: true, disabledReason: t('reason.actionInFlight') }
      : moving && estopped
        ? { disabled: true, disabledReason: t('reason.estopActive') }
        : confirmRoute && (kind === 'resume' || (kind === 'recover' && decision === 'resume'))
          ? { disabled: true, disabledReason: t('reason.resumeNeedsConfirm') }
          : rerun && !allChecked(resumeChecks, checked)
            ? { disabled: true, disabledReason: t('reason.physicalChecksRequired') }
            : { disabled: false }

  const submit = () => {
    if (confirmGate.disabled) return
    if (
      !validation.validate(
        reason,
        document.getElementById(reasonField.inputProps.id)?.parentElement ?? null,
      )
    )
      return
    const trimmed = reason.trim()
    const timed = timedShown ? { timedAtom } : {}
    if (kind === 'stop')
      /* 🔴 기본 사유는 원장에 남는 글자다 — 언어와 상관없이 같은 값이 나간다(`serverText`). 칸의 안내(`placeholder`)만 언어를 따른다 */
      onSubmit({
        kind: 'stop',
        reason: trimmed === '' ? serverText('strip.stopReasonDefault') : trimmed,
      })
    else if (kind === 'resume') onSubmit({ kind: 'resume', ...timed })
    else if (kind === 'recover')
      onSubmit({
        kind: 'recover',
        decision,
        reason: reasonRequired ? trimmed : undefined,
        ...(rerun ? { physicalChecked: true as const, physicalChecks: resumeChecks } : {}),
        ...timed,
      })
    else onSubmit({ kind: 'release', reason: trimmed, force: true })
  }

  /* 중지도 「확인」이다 — 본문 물음(「작업을 중지하시겠습니까?」)이 동작을 이미 말한다(eric 2026-10-26 「취소 확인 버튼」) */
  const confirmLabel =
    kind === 'recover' || kind === 'stop' ? t('action.confirm') : t(CONFIRM_LABEL[kind])
  /* 위험색은 되돌릴 수 없이 버리는 쪽(중지 · 반납 · 강제 해제 · 폐기)에만 */
  const danger = kind !== 'resume' && (kind !== 'recover' || decision === 'discard')

  return (
    <Modal
      footer={
        <>
          <Button onClick={onCancel} size="pill">
            {t('action.cancel')}
          </Button>
          <Button
            onClick={submit}
            size="pill"
            variant={danger ? 'danger' : 'primary'}
            {...confirmGate}
          >
            {confirmLabel}
          </Button>
        </>
      }
      onClose={onCancel}
      title={t(TITLE[kind])}
      size="sm"
      /*
       * 중지만 제목 앞에 느낌표 경고 삼각형(`Icon` `alert` · 위험색) — eric 2026-10-26 「경고 삼각형 경고표 작업 중지」 · 「경고 표시 삼각형 있잖아」
       * (글자 ▲ · 호박은 「약하다」로 바뀌었다).
       * 장식이라 스크린리더에는 제목 글자만 읽힌다. 색만으로 말하지 않는다 — 글리프와 제목 글자가 같이 말한다.
       */
      {...(kind === 'stop'
        ? {
            titleMark: (
              <span aria-hidden="true" className="strip-confirm__warn">
                <Icon name="alert" />
              </span>
            ),
          }
        : {})}
    >
      {/*
        🔴 **중지는 물음 한 줄이다**(eric 2026-10-26 「이거 다 빼고. 작업 중지 팝업 창에 '작업을 중지하시겠습니까?' 이렇게 하고..」).
           작업 이름 · 레시피 · 작업자 · 시작 · 「되돌리기 불가」 줄을 세웠다가 같은 날 걷었다 — 되살리지 마라.
        그 밖(복구 · 재개 · 락 반납)은 설명 문장 없이 대상과 성질을 **값으로만** 보인다(§1-1).
      */}
      {kind === 'stop' ? <p className="strip-confirm__ask">{t('strip.stopConfirmText')}</p> : null}
      {kind === 'stop' ? null : (
        <ValueRows
          rows={[
            ...(batchId === undefined
              ? []
              : [{ key: 'batch', label: t('column.batch'), value: batchId }]),
            /*
             * 🔴 「되돌리기 불가」는 되돌릴 수 없는 조작에만 — 서버 값이 아니라 이 대화가 다는 성질이다. 복구 › 재개는
             *    다시 멈추거나 중지할 수 있어 달지 않는다(eric 2026-10-14 「복구 판정에 되돌리기 불가라는 게.. 왜」).
             *    폐기(시료 종착) · 완료 확정(사람 선언 종착)은 되돌릴 경로가 없다. 사유 필수 여부와 같은 경계다.
             */
            ...(reasonRequired && !moving
              ? [{ key: 'reversible', label: t('row.reversible'), value: t('value.irreversible') }]
              : []),
          ]}
        />
      )}

      {/* 복구 판정 3택 — 대화 안에 펼친다(드롭다운은 대화 밑으로 잘렸다) */}
      {kind === 'recover' ? (
        <Field label={t('strip.recoverDecision')}>
          <Segmented
            label={t('strip.recoverDecision')}
            onChange={setDecision}
            options={decisions.map((d) => ({ value: d.value, label: t(d.labelKey) }))}
            value={decision}
          />
        </Field>
      ) : null}

      {/* 확인 재개 — 현장 확인 체크 목록(전부 체크해야 확인이 눌린다) */}
      {rerun ? (
        <CutAtomChecks checked={checked} checks={resumeChecks} onChange={setChecked} />
      ) : null}

      {/* 끊긴 시간 원자 방식 — 고를 것이 둘일 때만 */}
      {timedShown ? (
        <TimedAtomPick choices={timedAtomChoices} onChange={setTimedAtom} value={timedAtom} />
      ) : null}

      {/* 계약 필수값. 감사 원장(`audit_log.detail.reason`)에 해시체인으로 봉인된다 */}
      {reasonShown ? (
        <Field {...reasonField.fieldProps} label={t('field.reason')}>
          <TextInput
            {...reasonField.inputProps}
            onChange={(next) => {
              setReason(next)
              validation.change('reason', next)
            }}
            /* 중지: 안내 글 = 비웠을 때 보내는 기본 사유(같은 키 · eric 2026-10-26) */
            {...(kind === 'stop' ? { placeholder: t('strip.stopReasonDefault') } : {})}
            value={reason}
            wide
          />
        </Field>
      ) : null}

      {errorMessage === undefined ? null : (
        <p className="vissue" role="alert">
          {errorMessage}
        </p>
      )}

      {/*
        🔴 **확인이 꺼졌으면 그 까닭을 보이는 글자로**(eric 2026-10-29 「사유를 적었는데 확인이 활성화 안 돼?」).
           단추의 title · 스크린리더 글자만으로는 사용자가 왜 안 눌리는지 모른다. 단추 줄 바로 위 · 흑색 보조 글자(위험색 아님).
           글은 `confirmGate.disabledReason` 그대로다 — 새 문장을 짓지 않는다. 켜져 있으면 줄이 없다.
      */}
      {confirmGate.disabled ? (
        <p className="strip-confirm__gate muted">
          <span aria-hidden="true" className="strip-confirm__gate-mark">
            ⓘ
          </span>
          {confirmGate.disabledReason}
        </p>
      ) : null}
    </Modal>
  )
}
